/**
 * Epic 13 — Mochi for Tasks, the panel's side. Proposals are made by Mochi's
 * tools in the chat; applying, undoing, the daily briefing, "Find tasks" and
 * "Summarise" are explicit requests from the person (US 13.4-S).
 */
import { getValidAccessTokenFromStorage } from "@weco/suite-client";

const BASE = "/api/assistant/tasks-ai";

export class MochiError extends Error {
  constructor(message: string, public status: number, public code = "") {
    super(message);
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getValidAccessTokenFromStorage();
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* the server falls back to UTC */
  }
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(tz ? { "X-Timezone": tz } : {}), ...(init.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new MochiError(String(data.detail || data.message || res.status), res.status, String(data.code || ""));
  return data as T;
}

export type MochiLang = "en" | "ar";
export interface Person {
  email: string;
  name: string;
}
export interface SquadBrief {
  id: string;
  name: string;
  hub_id: string;
  hub_name: string;
  members: Person[];
}
export interface TaskBrief {
  id: string;
  key: string;
  title: string;
  done: boolean;
  priority: string;
  due_date: string | null;
  due_time: string | null;
  squad_name: string;
  assignees: Person[];
  blocked?: boolean;
}
export interface CreateItem {
  title: string;
  description: string;
  squad: SquadBrief | null;
  squad_choices: SquadBrief[];
  private: boolean;
  priority: string;
  category: string | null;
  assignees: Person[];
  ambiguous: Record<string, Person[]>;
  unknown_people: string[];
  due_date: string | null;
  due_time: string | null;
  repeat_rule: { kind: string } | null;
  remind_at: string | null;
  labels: string[];
  needs_squad: boolean;
  quote?: string;
}
export interface ChangeItem {
  task: TaskBrief;
  rows: { field: string; old: string | null; new: string | null }[];
}
export interface Proposal {
  card: "tasks_proposal";
  proposal_id: string;
  kind: "create" | "change";
  action?: "update" | "archive" | "delete";
  lang: MochiLang;
  items: (CreateItem | ChangeItem)[];
  squads?: SquadBrief[];
  questions?: string[];
  refused?: { task: TaskBrief; reason: string }[];
  confirm_count: number;
  large?: boolean;
}
export interface ApplyResult {
  ok: boolean;
  created?: TaskBrief[];
  updated?: TaskBrief[];
  failed: { title: string | null; detail: string }[];
  count?: number;
  undo_available: boolean;
  message?: string;
  code?: string;
}
export interface Briefing {
  lang: MochiLang;
  date: string;
  overdue: TaskBrief[];
  due_today: TaskBrief[];
  approvals: { id: string; task: { id: string; title?: string } | null }[];
  waiting: { name: string; task: { id: string; title?: string } | null }[];
  suggestions: { kind: "move_overdue" | "add_to_my_day"; due?: string; ids: string[] }[];
}
export interface Summary {
  status: string;
  decisions: { text: string; comment: string; by: string }[];
  open_questions: { text: string; comment: string; by: string }[];
  waiting: { who: string; on: string; comment: string; by: string }[];
  comment_count: number;
}

export const mochiApi = {
  apply: (id: string, body: { ticked?: number[]; edits?: Record<string, Partial<CreateItem>> }) =>
    req<ApplyResult>(`/proposals/${encodeURIComponent(id)}/apply/`, { method: "POST", body: JSON.stringify(body) }),
  undo: (id: string) => req<{ ok: boolean }>(`/proposals/${encodeURIComponent(id)}/undo/`, { method: "POST" }),
  briefing: (lang: MochiLang) => req<Briefing>(`/briefing/?lang=${lang}`),
  suggest: (body: { messages: { ref: string; author?: string; text: string }[]; source?: Record<string, unknown>; lang?: MochiLang; squad?: string | null }) =>
    req<Proposal | { card: null; message: string; items: [] }>("/suggest/", { method: "POST", body: JSON.stringify(body) }),
  /** US 13.6 — suggestions from a transcribed meeting: by its id (from the
   *  Inbox) or by the event / room it was for (the meeting notes panel). */
  meeting: (target: { id: string } | { ref: string }) =>
    req<Proposal | { card: null; message: string; items: []; code?: string }>(
      "id" in target ? `/meeting/${encodeURIComponent(target.id)}/` : `/meeting/?ref=${encodeURIComponent(target.ref)}`,
    ),
  summarize: (taskId: string, lang?: MochiLang) =>
    req<{ summary: Summary; lang: MochiLang; comment_body: string }>("/summarize/", { method: "POST", body: JSON.stringify({ task_id: taskId, lang }) }),
  postSummary: (taskId: string, body: string) => req<unknown>("/summarize/post/", { method: "POST", body: JSON.stringify({ task_id: taskId, body }) }),
};
